import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBed,
  faBookmark,
  faCalendarDays,
  faClock,
  faLocationDot,
  faWallet,
  faMobileScreenButton,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { Flag, MaskIcon, buildSegments } from './SharedBits';
import { TransportTab, StaysTab, PlacesTab, BudgetTab } from './SharedTabs';
import SharedMap from './SharedMap';
import { WEB_APP_URL } from '../../utils/webApp';
import './SharedItinerary.css';

/**
 * Shared trip link (/trip/:id?s=token) for people without the app. A web port of the app's shared
 * view (view-itinerary.tsx in shared mode): same hero, tabs, timeline cards and copy bar, so a
 * recipient sees the same thing on either surface.
 *
 * `share` is the mobile project's getSharedTrip result: already sanitized (no booking details,
 * members or real dates; every date sits on the 1900 anchor and reads as "Day N") and grouped
 * into `days`. Copying needs the app, so the call to action is "Get VOIA".
 */

const PUBLIC = process.env.PUBLIC_URL || '';
const DEFAULT_COVER = `${PUBLIC}/assets/images/trip-cover-default.jpg`;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function monthLabel(month) {
  const m = /^(\d{4})-(\d{2})$/.exec(month || '');
  return m ? `${MONTHS[Number(m[2]) - 1]} ${m[1]}` : 'Flexible dates';
}

function platform() {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
}

const nightsText = (n) => (n === 1 ? '1 night' : `${n} nights`);

const Rail = ({ label }) => (
  <div className="si-rail">
    <span className="si-rail-line" />
    <span className="si-rail-label">{label}</span>
    <span className="si-rail-line" />
  </div>
);

const DayVisitCard = ({ dv }) => (
  <div className="si-dv-card">
    {dv.photoUrl ? <img className="si-dv-photo" src={dv.photoUrl} alt="" /> : <span className="si-dv-photo si-photo-empty" />}
    <div className="si-dv-text">
      <span className="si-dv-name">{dv.name}</span>
      <span className="si-dv-meta">
        <Flag name={dv.name} />
        <span className="si-tag">Day Visit</span>
      </span>
    </div>
  </div>
);

/** Day visits on days [from, to] of a segment, as the app's day-visit panel. */
const DvPanel = ({ days, seg, from, to, headers, stay }) => {
  const groups = [];
  for (let off = from; off <= to; off += 1) {
    const day = days[seg.start + off];
    if (!day || day.dayVisits.length === 0) continue;
    groups.push(
      <div key={off} className="si-dv-group">
        {headers && (
          <div className="si-dv-rail">
            <span className="si-dv-rail-line" />
            <span className="si-dv-rail-label">Day {seg.start + off + 1}</span>
            <span className="si-dv-rail-line" />
          </div>
        )}
        {day.dayVisits.map((dv, k) => <DayVisitCard key={k} dv={dv} />)}
      </div>,
    );
  }
  if (groups.length === 0) return null;
  return <div className={`si-dv-panel${stay ? ' si-dv-panel-stay' : ''}`}>{groups}</div>;
};

/** Read-only timeline: a port of the app's TripTimelineView for a flexible ("Day N") trip. */
const Timeline = ({ days }) => {
  const segs = useMemo(() => buildSegments(days), [days]);
  if (segs.length === 0) return <p className="si-empty">Nothing planned yet.</p>;
  const out = [<Rail key="top" label={`Day ${segs[0].start + 1}`} />];
  segs.forEach((seg, idx) => {
    const isLast = idx === segs.length - 1;
    if (seg.kind === 'transit') {
      out.push(<DvPanel key={`tp${idx}`} days={days} seg={seg} from={0} to={seg.len - 1} />);
      out.push(
        <div key={`t${idx}`} className="si-transit-card">
          <MaskIcon name="transportation" size={16} />
          <span>In Transit · {nightsText(seg.nights)}</span>
        </div>,
      );
    } else {
      out.push(<DvPanel key={`ci${idx}`} days={days} seg={seg} from={0} to={0} stay />);
      out.push(
        <div key={`s${idx}`} className="si-stop-card">
          {seg.photoUrl ? <img className="si-stop-photo" src={seg.photoUrl} alt="" /> : <span className="si-stop-photo si-photo-empty" />}
          <div className="si-stop-body">
            <span className="si-stop-name">{seg.name}</span>
            <span className="si-stop-meta">
              <Flag name={seg.name} />
              <span className="si-nights-pill">{nightsText(seg.nights)}</span>
            </span>
          </div>
        </div>,
      );
      out.push(<DvPanel key={`r${idx}`} days={days} seg={seg} from={1} to={seg.len - 1} headers stay />);
    }
    const next = seg.start + seg.len + 1;
    out.push(<Rail key={`a${idx}`} label={isLast ? `Day ${next} · Trip end` : `Day ${next}`} />);
    if (isLast) out.push(<DvPanel key="end" days={days} seg={seg} from={seg.len} to={seg.len} />);
  });
  return <div className="si-timeline">{out}</div>;
};

const FlagStack = ({ countries }) => {
  if (countries.length === 0) return null;
  const visible = countries.slice(0, 5);
  const overflow = countries.length - visible.length;
  return (
    <div className="si-flag-stack">
      {visible.map((c, i) => (
        <span key={`${c}-${i}`} className="si-flag-stack-item">
          <Flag name={c} className="si-flag-stack-img" />
        </span>
      ))}
      {overflow > 0 && <span className="si-flag-stack-more">+{overflow}</span>}
    </div>
  );
};

const APP_STORE_URL = process.env.REACT_APP_APP_STORE_URL || 'https://apps.apple.com/pt/app/voia-travel/id6747190886?l=en-GB';
const PLAY_STORE_URL = process.env.REACT_APP_PLAY_STORE_URL || 'https://play.google.com/store/apps/details?id=com.voia.travel.mobile';

/** Desktop only: VOIA can't be installed here, so point to both stores and the phone. */
const GetAppSheet = ({ token, onClose }) => {
  const badge = (store) => {
    const ios = store === 'ios';
    const label = ios ? 'Download on the App Store' : 'Get it on Google Play';
    return (
      <a
        key={store}
        href={ios ? APP_STORE_URL : PLAY_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="si-store-link"
        aria-label={label}
        data-track-source="trip_share"
        data-share-token={token}
      >
        <img src={`${PUBLIC}/assets/${ios ? 'app_store_badge.svg' : 'google_play_badge.png'}`} alt={label} className="si-store-badge" />
      </a>
    );
  };
  return (
    <div className="si-sheet-backdrop" onClick={onClose} role="presentation">
      <div className="si-sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Get VOIA">
        <button type="button" className="si-sheet-close" onClick={onClose} aria-label="Close">
          <FontAwesomeIcon icon={faXmark} />
        </button>
        <img className="si-sheet-icon" src={`${PUBLIC}/logo192.png`} alt="VOIA" />
        <h2 className="si-sheet-title">Plan your own trips in VOIA</h2>
        <p className="si-sheet-text">Build your itineraries, plan with friends and keep every trip in one place.</p>
        <p className="si-sheet-note">Install VOIA, then open this link on your phone.</p>
        <div className="si-sheet-stores">{badge('ios')}{badge('android')}</div>
      </div>
    </div>
  );
};

const SharedItinerary = ({ tripId, token, share }) => {
  const trip = share.trip || {};
  const days = useMemo(() => (Array.isArray(share.days) ? share.days : []), [share.days]);
  const countries = Array.isArray(trip.countries) ? trip.countries : [];
  // Full-size cover first: the hero spans the window, so a thumbnail would be stretched.
  const cover = trip.coverPhoto || trip.coverPhotoThumbnail || DEFAULT_COVER;
  // Same owner line as the map header: username and profile photo.
  const ownerName = share.owner?.username || share.owner?.firstName || 'a VOIA traveler';
  const ownerPhoto = share.owner?.photoUrl
    || `https://ui-avatars.com/api/?name=${encodeURIComponent(ownerName)}&background=random&color=fff`;
  const commutes = Array.isArray(trip.commutes) ? trip.commutes : [];
  const stays = Array.isArray(trip.accommodations) ? trip.accommodations : [];
  const tours = Array.isArray(trip.tours) ? trip.tours : [];
  const places = Array.isArray(share.places) ? share.places : [];
  const budget = Array.isArray(share.budget) ? share.budget : [];

  // Same rule as the app: a tab shows only when it has something, no tab bar for the timeline alone.
  const tabs = [
    { key: 'timeline', icon: <FontAwesomeIcon icon={faLocationDot} />, label: 'Itinerary' },
    commutes.length > 0 && { key: 'transports', icon: <MaskIcon name="transportation" size={24} />, label: 'Transport' },
    stays.length > 0 && { key: 'accommodations', icon: <FontAwesomeIcon icon={faBed} />, label: 'Stays' },
    (places.length > 0 || tours.length > 0) && { key: 'tours', icon: <MaskIcon name="star" size={20} />, label: 'Places' },
    budget.length > 0 && { key: 'expenses', icon: <FontAwesomeIcon icon={faWallet} />, label: 'Budget' },
  ].filter(Boolean);
  const [tab, setTab] = useState('timeline');
  const [sheetOpen, setSheetOpen] = useState(false);
  const os = platform();
  const openInApp = `voia-travel-mobile://view-trip?tripId=${encodeURIComponent(tripId)}&s=${token}`;
  // Desktop: the web app opens this same shared view after sign-in, with its Save button.
  const saveOnWeb = `${WEB_APP_URL}/shared-itinerary?tripId=${encodeURIComponent(tripId)}&s=${token}`;
  const [mapOpen, setMapOpen] = useState(false);

  // The map is a screen of its own, so the browser back button closes it (as back does in the app).
  useEffect(() => {
    const onPop = () => setMapOpen(window.location.hash === '#map');
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const openMap = () => {
    window.history.pushState(null, '', '#map');
    setMapOpen(true);
  };
  const closeMap = () => {
    if (window.location.hash === '#map') window.history.back();
    else setMapOpen(false);
  };

  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'trip_share_opened', { surface: 'web', trip_id: tripId });
    }
  }, [tripId]);

  useEffect(() => {
    if (trip.name) document.title = `${trip.name} | VOIA`;
  }, [trip.name]);

  const nDays = days.length;
  return (
    <div className="si-shell">
      <div className="si-page">
        <header className="si-hero">
          {/* A blurred copy fills the window width; the sharp photo keeps the app's framing in the column. */}
          <img className="si-hero-backdrop" src={cover} alt="" aria-hidden="true" />
          <img className="si-hero-img" src={cover} alt="" />
          <div className="si-hero-overlay" />
          <div className="si-hero-info">
            <h1 className="si-trip-name">{trip.name || 'Untitled Trip'}</h1>
            <div className="si-trip-meta">
              <span className="si-meta-item"><FontAwesomeIcon icon={faCalendarDays} />{monthLabel(share.month)}</span>
              <span className="si-meta-item"><FontAwesomeIcon icon={faClock} />{nDays} day{nDays === 1 ? '' : 's'}</span>
              <span className="si-meta-item"><img className="si-owner-avatar" src={ownerPhoto} alt="" />by {ownerName}</span>
            </div>
            <FlagStack countries={countries} />
          </div>
        </header>

        <main className="si-content">
          {trip.description ? <p className="si-description">{trip.description}</p> : null}

          {tabs.length > 1 && (
            <nav className="si-tabbar" role="tablist">
              {tabs.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.key}
                  aria-label={t.label}
                  className={`si-tab${tab === t.key ? ' si-tab-active' : ''}`}
                  onClick={() => setTab(t.key)}
                >
                  {t.icon}
                </button>
              ))}
            </nav>
          )}

          {tab === 'timeline' && <Timeline days={days} />}
          {tab === 'transports' && <TransportTab commutes={commutes} trip={trip} />}
          {tab === 'accommodations' && <StaysTab stays={stays} trip={trip} upcoming={share.upcoming} />}
          {tab === 'tours' && <PlacesTab places={places} tours={tours} trip={trip} />}
          {tab === 'expenses' && <BudgetTab budget={budget} trip={trip} />}

          <p className="si-privacy">Shared with VOIA. Exact dates and booking details stay private.</p>
          {/* After installing, this is the way back into the trip (same-domain taps don't open the app). */}
          {os !== 'desktop' && (
            <a className="si-open-app" href={openInApp}>Have VOIA? Open this trip in the app</a>
          )}
          {os === 'desktop' && (
            <button type="button" className="si-open-app si-get-app-link" onClick={() => setSheetOpen(true)}>
              Get the VOIA app
            </button>
          )}
        </main>

      </div>
      {!mapOpen && (
        <>
          {/* Floating pair over the hero: install on the left, the map on the right (as in the app). */}
          {os === 'desktop' ? (
            <a
              className="si-float-button si-install-button"
              href={saveOnWeb}
              data-track-location="shared_trip_save"
            >
              <FontAwesomeIcon icon={faBookmark} />
              Save this trip
            </a>
          ) : (
            <a
              className="si-float-button si-install-button"
              href={os === 'ios' ? APP_STORE_URL : PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-track-source="trip_share"
              data-share-token={token}
            >
              <FontAwesomeIcon icon={faMobileScreenButton} />
              Get the app
            </a>
          )}
          <button type="button" className="si-float-button si-map-button" onClick={openMap}>
            <MaskIcon name="globe" size={20} />
            Itinerary Map
          </button>
        </>
      )}
      {mapOpen && <SharedMap trip={trip} places={places} owner={share.owner} onClose={closeMap} />}
      {sheetOpen && <GetAppSheet token={token} onClose={() => setSheetOpen(false)} />}
    </div>
  );
};

export default SharedItinerary;
